import React, { useState } from "react";
import "./AddDriver.css";
import ApiManager from "../../ApiManager/ApiManager";
import { useNavigate } from "react-router-dom";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

const AddDriver = () => {
  const navigate = useNavigate();
  const { withLoading, isLoading } = useComponentLoading();
  const [salaryType, setSalaryType] = useState("fixed");

  const convertTimeToNumber = (inputTime) => {
    if (inputTime) {
      let [time, modifier] = inputTime.split(" ");
      let [hours, minutes] = time.split(":");

      hours = Number(hours);
      minutes = Number(minutes);

      if (modifier?.toLowerCase() === "pm" && hours !== 12) {
        hours += 12;
      } else if (modifier?.toLowerCase() === "am" && hours === 12) {
        hours = 0;
      }

      const formattedTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:00`;
      return formattedTime;
    }
    return null;
  };

  const convertDateToString = (inputDate) => {
    if (!inputDate) return null;

    const parts = inputDate.split("-");

    if (parts.length !== 3) return null;
    if (parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    } else {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  };

  const isValidPhoneNumber = (num) => /^[6-9]\d{9}$/.test(num);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get form data
    const formData = new FormData(e.target);
    const driverData = Object.fromEntries(formData.entries());

    // Convert working times
    const startTimeNumber = convertTimeToNumber(driverData.working_start_time);
    const endTimeNumber = convertTimeToNumber(driverData.working_end_time);

    // Convert driverData to the correct structure
    const payload = {
      username: driverData.username,
      firstName: driverData.firstName,
      lastName: driverData.lastName,
      email: driverData.email,
      password: driverData.password,
      address: driverData.address,
      birthDate: convertDateToString(driverData.birthDate),
      joinDate: convertDateToString(driverData.joinDate),
      phone: driverData.phone,
      licenseNo: driverData.licenseNo,
      licenseExp: convertDateToString(driverData.licenseExp),
      driverSalaryInfo: {
        salaryType: salaryType,
        salary: parseFloat(driverData.salary),
        daily_wage: parseFloat(driverData.daily_wage),
        working_start_time: startTimeNumber,
        working_end_time: endTimeNumber,
        daily_allowance: parseFloat(driverData.daily_allowance),
        overtime_per_hour: parseFloat(driverData.overtime_per_hour),
        outstation_allowance: parseFloat(driverData.outstation_allowance),
        sunday_allowance: parseFloat(driverData.sunday_allowance),
        night_allowance: parseFloat(driverData.night_allowance),
        past_salary_info: {
          paid_till: convertDateToString(driverData.paid_till),
          left_over_amount: parseFloat(driverData.left_over_amount),
        }
      },
    };

    try {
      await withLoading('addDriver', async (onProgress) => {
        onProgress(20, 'Preparing driver data...');
        console.log("Payload:", payload);
        
        onProgress(50, 'Creating driver...');
        const response = await ApiManager.addDriver(payload);
        console.log("Response:", response);
        
        if (!response.ok) throw new Error(await response.text());
        
        onProgress(80, 'Driver created successfully');
        navigate('/login/drivers');
        onProgress(100, 'Redirecting...');
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(error.message || "Failed to add driver");
    }
  };

  return (
    <div className="addDriver">
      <div>
        <h3>Add Driver</h3>
      </div>
      <form onSubmit={handleSubmit}>
        {/* Driver Details Section */}
        <fieldset className="fieldset">
          <legend>Driver Details</legend>
          <div className="row">
            <div className="formGroup">
              <label>Username *</label>
              <input
                type="text"
                name="username"
                placeholder="Enter username"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>First Name *</label>
              <input
                type="text"
                name="firstName"
                placeholder="Enter first name"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Last Name *</label>
              <input
                type="text"
                name="lastName"
                placeholder="Enter last name"
                required
                className="input"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Email *</label>
              <input
                type="email"
                name="email"
                placeholder="Enter email"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Password *</label>
              <input
                type="password"
                name="password"
                placeholder="Enter password"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Address *</label>
              <input
                type="text"
                name="address"
                placeholder="Enter address"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Phone *</label>
              <input
                type="text"
                name="phone"
                placeholder="Enter phone number"
                required
                className="input"
                onChange={(e) => {
                  e.target.setCustomValidity(isValidPhoneNumber(e.target.value) ? "" : "Please enter a valid 10-digit phone number");
                }}
                onInvalid={(e) => e.target.setCustomValidity("Please enter a valid 10-digit phone number")}
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Birth Date *</label>
              <input
                type="date"
                name="birthDate"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Join Date *</label>
              <input
                type="date"
                name="joinDate"
                required
                className="input"
              />
            </div>
          </div>
        </fieldset>

        {/* License Information Section */}
        <fieldset className="fieldset">
          <legend>License Information</legend>
          <div className="row">
            <div className="formGroup">
              <label>License No *</label>
              <input
                type="text"
                name="licenseNo"
                placeholder="Enter license number"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>License Expiry *</label>
              <input
                type="date"
                name="licenseExp"
                required
                className="input"
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="fieldset">
          <legend>Past Salary Info</legend>
          <div className="row">
            <div className="formGroup">
              <label>Paid Till *</label>
              <input
                type="date"
                name="paid_till"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Left over amount *</label>
              <input
                type="number"
                name="left_over_amount"
                required
                className="input"
              />
            </div>
          </div>
        </fieldset>

        {/* Salary Information Section */}
        <fieldset className="fieldset">
          <legend>Salary Information</legend>
          <div className="row">
            <div className="formGroup">
              <label>Salary Type *</label>
              <select
                name="salaryType"
                value={salaryType}
                onChange={(e) => setSalaryType(e.target.value)}
                required
                className="input"
              >
                <option value="fixed">Fixed</option>
                <option value="daily">Daily Wage</option>
              </select>
            </div>
            <div className="formGroup">
              <label>Salary *</label>
              <input
                type="number"
                name="salary"
                placeholder="Enter salary"
                required
                className="input"
                step="0.01"
              />
            </div>
            <div className="formGroup">
              <label>Daily Wage *</label>
              <input
                type="number"
                name="daily_wage"
                placeholder="Enter daily wage"
                required
                className="input"
                step="0.01"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Working Start Time *</label>
              <input
                type="time"
                name="working_start_time"
                placeholder="Enter start time"
                required
                className="input"
              />
            </div>
            <div className="formGroup">
              <label>Working End Time *</label>
              <input
                type="time"
                name="working_end_time"
                placeholder="Enter end time"
                required
                className="input"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Daily Allowance *</label>
              <input
                type="number"
                name="daily_allowance"
                placeholder="Enter daily allowance"
                required
                className="input"
                step="0.01"
              />
            </div>
            <div className="formGroup">
              <label>Overtime Per Hour *</label>
              <input
                type="number"
                name="overtime_per_hour"
                placeholder="Enter overtime per hour"
                required
                className="input"
                step="0.01"
              />
            </div>
          </div>

          <div className="row">
            <div className="formGroup">
              <label>Outstation Allowance *</label>
              <input
                type="number"
                name="outstation_allowance"
                placeholder="Enter outstation allowance"
                required
                className="input"
                step="0.01"
              />
            </div>
            <div className="formGroup">
              <label>Sunday Allowance *</label>
              <input
                type="number"
                name="sunday_allowance"
                placeholder="Enter sunday allowance"
                required
                className="input"
                step="0.01"
              />
            </div>
            <div className="formGroup">
              <label>Night Allowance *</label>
              <input
                type="number"
                name="night_allowance"
                placeholder="Enter night allowance"
                required
                className="input"
                step="0.01"
              />
            </div>
          </div>
        </fieldset>

        {/* Submit Button */}
        <button 
          type="submit" 
          className="submitButton"
          disabled={isLoading('addDriver')}
        >
          {isLoading('addDriver') ? (
            <LoadingSpinner 
              size="xs"
              inline={true}
            />
          ) : (
            'Submit'
          )}
        </button>
      </form>
    </div>
  );
};

export default AddDriver;
